// P-085 · cómo cuenta Plataforma el recorrido de cada tester y el embudo de la beta (puro). El servidor decide el paso,
// las fechas y los contadores (docs/p085/ESTADOS-P085.md §2, FUNNEL-P085.md §3); aquí solo se dicen en palabras.

export type BetaJourneyStep =
  | 'REVOKED' | 'INVITE_EXPIRED' | 'ACCOUNT_EXPIRED' | 'INVITED' | 'LINK_OPENED' | 'ACCOUNT_CREATED'
  | 'BUSINESS_CREATED' | 'OFFERING_SELECTED' | 'ITEM_SKIPPED' | 'FIRST_ITEM' | 'POS_OPENED' | 'AHA';

export interface BetaJourney {
  step: BetaJourneyStep;
  openedAt: string | null;
  acceptedAt: string | null;
  businessCreatedAt: string | null;
  offering: string | null;
  offeringAt: string | null;
  itemSkippedAt: string | null;
  firstItemAt: string | null;
  posOpenedAt: string | null;
  firstSaleAt: string | null;
  lastProgressAt: string | null;
  lastActivityAt: string | null;
  resumeCount: number;
  lastError: { step: string; code: string; at: string; count: number } | null;
}

export interface BetaCommercial { mode: 'PRIVATE_BETA' | 'MANAGED' | 'SELF_SERVE'; status: string | null; trialEndsAt: string | null }

export interface BetaFunnelSummary {
  cohort: number;
  listLimit: number;
  opened: number;
  accepted: number;
  businessCreated: number;
  offeringSelected: number;
  firstItem: number;
  posOpened: number;
  aha: number;
  medianMinutes: { inviteToAccount: number | null; accountToBusiness: number | null; businessToFirstItem: number | null; businessToAha: number | null };
}

export const STEP_LABEL: Record<BetaJourneyStep, string> = {
  REVOKED: 'Revocada',
  INVITE_EXPIRED: 'Invitación vencida',
  ACCOUNT_EXPIRED: 'Cuenta sin terminar',
  INVITED: 'Invitado, sin abrir',
  LINK_OPENED: 'Abrió el enlace',
  ACCOUNT_CREATED: 'Cuenta creada',
  BUSINESS_CREATED: 'Negocio creado',
  OFFERING_SELECTED: 'Eligió qué vende',
  ITEM_SKIPPED: 'Pospuso su primer artículo',
  FIRST_ITEM: 'Primer artículo listo',
  POS_OPENED: 'Abrió el punto de venta',
  AHA: 'Primera venta hecha',
};

const ERROR_STEP_LABEL: Record<string, string> = {
  invite_link: 'al abrir el enlace',
  create_account: 'al crear la cuenta',
  resume: 'al retomar',
  setup: 'al preparar el negocio',
  offering: 'al elegir qué vende',
  first_item: 'en el primer artículo',
  activation: 'al cargar la guía',
  pos: 'en el punto de venta',
};

export const COMMERCIAL_LABEL: Record<BetaCommercial['mode'], string> = {
  PRIVATE_BETA: 'Beta sin costo',
  MANAGED: 'Administrado',
  SELF_SERVE: 'Autoservicio',
};

/** Una duración en minutos dicha en palabras: «8 min», «3 h», «2 días». null = sin datos. Pura. */
export function durationLabel(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} min`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (24 * 60))} días`;
}

/** «Último error: al preparar el negocio · SIGNUP_SESSION_REQUIRED (2)». Pura. */
export function lastErrorLabel(error: BetaJourney['lastError']): string | null {
  if (!error) return null;
  const where = ERROR_STEP_LABEL[error.step] ?? error.step;
  return `Último error ${where} · ${error.code}${error.count > 1 ? ` (${error.count})` : ''}`;
}

/** Los pasos del embudo, en orden, con su contador. Pura. */
export function funnelRows(summary: BetaFunnelSummary): Array<{ label: string; value: number }> {
  return [
    { label: 'Invitados', value: summary.cohort },
    { label: 'Abrieron', value: summary.opened },
    { label: 'Crearon cuenta', value: summary.accepted },
    { label: 'Negocio creado', value: summary.businessCreated },
    { label: 'Qué vende', value: summary.offeringSelected },
    { label: 'Primer artículo', value: summary.firstItem },
    { label: 'Abrieron el POS', value: summary.posOpened },
    { label: 'Primera venta', value: summary.aha },
  ];
}

/** Las medianas de tiempo entre hitos, dichas en palabras. Pura. */
export function medianRows(summary: BetaFunnelSummary): Array<{ label: string; value: string }> {
  const m = summary.medianMinutes;
  return [
    { label: 'Invitación → cuenta', value: durationLabel(m.inviteToAccount) },
    { label: 'Cuenta → negocio', value: durationLabel(m.accountToBusiness) },
    { label: 'Negocio → primer artículo', value: durationLabel(m.businessToFirstItem) },
    { label: 'Negocio → primera venta', value: durationLabel(m.businessToAha) },
  ];
}
